import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useContent } from "../../context/ContentContext";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import WhatsAppButton from "../../components/WhatsAppButton";

function CtaButton({ label = "Quiero mover a mi equipo" }) {
  return (
    <Link
      to="/contact"
      className="btn-shine inline-flex items-center gap-3 rounded-full bg-[#ff6a2b] hover:bg-[#ff8a3c] text-black pl-6 pr-5 py-3.5 font-mono-caps text-xs tracking-[0.2em] uppercase transition-colors"
    >
      {label}
      <span className="w-7 h-7 rounded-full bg-black text-white flex items-center justify-center">
        <ArrowRight className="w-3.5 h-3.5" />
      </span>
    </Link>
  );
}

export default function TeamsCuentaConmigoPage() {
  const { content, loading } = useContent();
  if (loading || !content) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-white/60 font-mono-caps tracking-widest">
        CARGANDO...
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-black text-white overflow-x-hidden">
      <Header />

      {/* HERO — Cuenta Conmigo (H1) */}
      <section className="relative pt-36 md:pt-44 pb-14 md:pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#ff6a2b]/12 via-black to-black" />
        <div className="absolute -top-32 -right-20 w-[520px] h-[520px] rounded-full bg-[#ff6a2b]/18 blur-[130px] pointer-events-none" />
        <div className="absolute inset-0 noise-overlay pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto">
          <p className="font-mono-caps text-xs md:text-sm tracking-[0.3em] uppercase text-[#ff8a3c] mb-6">Kilombo Teams</p>
          <h1
            className="font-display font-black text-[34px] leading-[1.05] sm:text-5xl md:text-6xl lg:text-[76px] xl:text-[84px] tracking-tight max-w-5xl"
            style={{ wordBreak: "keep-all", overflowWrap: "normal" }}
          >
            <span className="text-white">Cuenta Conmigo</span>
            <span className="text-[#ff8a3c] align-super text-[0.35em] ml-1">™</span>
            <span className="block mt-3 sm:mt-4 text-white/85 text-[22px] sm:text-3xl md:text-4xl lg:text-[42px] font-display font-bold leading-tight">
              El <span className="text-gradient-music">sistema operativo</span> de activación de cultura organizacional.
            </span>
          </h1>
          <p className="mt-10 md:mt-14 max-w-3xl text-white/85 text-lg md:text-2xl leading-snug font-light">
            La cultura no se construye en una mañana. <span className="text-white font-semibold">Se acompaña durante el año.</span>
          </p>
        </div>
      </section>

      {/* CONTENIDO */}
      <section className="relative bg-black py-14 md:py-20 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Bloque — ¿Qué es? */}
          <div className="rounded-2xl border border-white/10 bg-neutral-950 p-6 md:p-10">
            <p className="font-mono-caps text-xs tracking-[0.3em] uppercase text-[#ff8a3c] mb-4">— ¿Qué es? —</p>
            <p className="text-white/85 text-base md:text-lg leading-relaxed max-w-4xl">
              <span className="font-display font-bold text-white">Cuenta Conmigo</span> es un programa anual que activa la cultura organizacional{" "}
              <span className="text-[#ff8a3c] font-semibold">desde lo individual hacia lo colectivo</span>. Diseñamos y facilitamos experiencias que sostienen el pulso del equipo mes a mes, combinando disciplinas y recursos que casi nunca se tocan.
            </p>
            <div className="mt-8 flex flex-wrap gap-2.5">
              {["Innovación", "Música", "Arte", "Creatividad", "Coaching empresarial"].map((chip) => (
                <span
                  key={chip}
                  className="inline-flex items-center gap-2 rounded-full border border-[#ff8a3c]/40 bg-[#ff6a2b]/10 px-4 py-2 text-white/90 font-mono-caps text-[10px] md:text-xs tracking-[0.18em] uppercase"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#ff8a3c]" />
                  {chip}
                </span>
              ))}
            </div>
          </div>

          {/* CTA intermedio */}
          <div className="mt-8 md:mt-10">
            <CtaButton label="Quiero mover a mi equipo" />
          </div>

          {/* Bloque — El Terreno */}
          <div className="mt-14 md:mt-20">
            <p className="font-mono-caps text-xs tracking-[0.3em] uppercase text-[#ff8a3c] mb-6">
              EL TERRENO · <span className="text-white/50">DOS COSAS QUE NO SON LO MISMO</span>
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              {[
                { title: "Cultura", desc: "Quiénes somos. La declaración, los valores, lo que permanece. Se construye despacio." },
                { title: "Clima", desc: "Cómo nos relacionamos hoy. La percepción, lo visible, lo que cambia semana a semana." },
              ].map((c) => (
                <div
                  key={c.title}
                  className="relative rounded-2xl border border-white/10 bg-neutral-950 p-6 md:p-8 hover:border-[#ff8a3c]/40 transition-colors overflow-hidden"
                >
                  <span className="absolute top-0 left-6 right-6 h-[3px] rounded-b-full bg-[#ff8a3c]" />
                  <span className="absolute -top-16 -right-16 w-40 h-40 rounded-full blur-3xl pointer-events-none opacity-25 bg-[#ff6a2b]" />
                  <div className="relative">
                    <h3 className="font-display font-bold text-2xl md:text-3xl text-[#ffb46b] leading-tight pt-2">{c.title}</h3>
                    <p className="mt-4 text-white/75 text-base leading-relaxed">{c.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-6 text-white/70 text-base md:text-lg leading-relaxed max-w-4xl">
              Trabajamos las dos: la cultura para que el equipo sepa <span className="text-white font-semibold">quién es</span>, el clima para que <span className="text-white font-semibold">quiera estar ahí</span>.
            </p>
          </div>

          {/* Bloque — 01 · El Punto de Partida (Matriz 2x2) */}
          <div className="mt-14 md:mt-20">
            <p className="font-mono-caps text-xs tracking-[0.3em] uppercase text-[#ff8a3c] mb-6">
              01 · <span className="text-white/50">El punto de partida</span>
            </p>
            <h3 className="font-display font-black text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.05] max-w-4xl text-white">
              Casi todo el team building mueve el ánimo de un día. Falta mover el <span className="text-gradient-music">resultado de todo el año</span>.
            </h3>

            <div className="mt-10 md:mt-14 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
              {[
                { axes: "↑ SATISFACCIÓN · ↑ RESULTADOS", title: "Productiva y sostenible", note: "Aquí jugamos", highlight: true },
                { axes: "↓ SATISFACCIÓN · ↑ RESULTADOS", title: "Resultados que queman a la gente" },
                { axes: "↑ SATISFACCIÓN · ↓ RESULTADOS", title: "Buen clima, sin resultados" },
                { axes: "↓ SATISFACCIÓN · ↓ RESULTADOS", title: "Ni ánimo ni resultados" },
              ].map((q, i) => (
                <div
                  key={i}
                  className={`relative rounded-2xl p-6 md:p-8 transition-colors overflow-hidden ${
                    q.highlight
                      ? "border-2 border-[#ff8a3c] bg-gradient-to-br from-[#ff6a2b]/20 via-black to-black"
                      : "border border-white/10 bg-neutral-950 hover:border-white/25"
                  }`}
                >
                  {q.highlight && (
                    <span className="absolute -top-16 -right-16 w-40 h-40 rounded-full blur-3xl pointer-events-none opacity-40 bg-[#ff6a2b]" />
                  )}
                  <div className="relative">
                    <p className={`font-mono-caps text-[10px] md:text-xs tracking-[0.2em] uppercase ${q.highlight ? "text-[#ffb46b]" : "text-white/40"}`}>
                      {q.axes}
                    </p>
                    <h4 className={`mt-4 font-display font-bold text-lg md:text-xl leading-snug pt-1 ${q.highlight ? "text-white" : "text-white/85"}`}>
                      {q.title}
                    </h4>
                    {q.note && (
                      <p className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#ff8a3c]/50 bg-[#ff6a2b]/10 px-3 py-1.5 text-[#ffb46b] font-mono-caps text-[10px] md:text-xs tracking-[0.18em] uppercase">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#ff8a3c]" />
                        {q.note}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bloque — 02 · Por qué un equipo actúa (Venn) */}
          <div className="mt-14 md:mt-20">
            <p className="font-mono-caps text-xs tracking-[0.3em] uppercase text-[#ff8a3c] mb-6">
              02 · <span className="text-white/50">Por qué un equipo actúa (o no)</span>
            </p>
            <h3 className="font-display font-black text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.05] max-w-4xl text-white">
              Un equipo pasa a la <span className="text-gradient-music">acción</span> donde se cruzan tres cosas.
            </h3>

            <div className="mt-12 md:mt-16 relative mx-auto" style={{ maxWidth: "640px" }}>
              <div className="relative w-full" style={{ aspectRatio: "1 / 1" }}>
                <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full" aria-hidden="true">
                  <circle cx="200" cy="150" r="140" fill="rgba(255,138,60,0.22)" stroke="#ff8a3c" strokeWidth="2" />
                  <circle cx="130" cy="255" r="140" fill="rgba(212,178,60,0.22)" stroke="#d4b23c" strokeWidth="2" />
                  <circle cx="270" cy="255" r="140" fill="rgba(46,201,168,0.22)" stroke="#2ec9a8" strokeWidth="2" />
                </svg>

                {/* Foco — zona exclusiva superior del círculo naranja */}
                <div className="absolute text-center" style={{ top: "10%", left: "50%", transform: "translateX(-50%)", width: "44%" }}>
                  <p className="font-display font-bold text-sm md:text-lg text-[#ffb46b] leading-tight">Foco</p>
                  <p className="text-[10px] md:text-xs text-white/85 leading-snug mt-1">Propósito, sentido, alineación</p>
                </div>

                {/* Capacidades — zona exclusiva izquierda del círculo amarillo */}
                <div className="absolute text-center" style={{ top: "70%", left: "0%", width: "30%" }}>
                  <p className="font-display font-bold text-sm md:text-lg text-[#e6c852] leading-tight">Capacidades</p>
                  <p className="text-[10px] md:text-xs text-white/85 leading-snug mt-1">Recursos, talentos, Habilidades, Estructura, organización</p>
                </div>

                {/* Deseo — zona exclusiva derecha del círculo verde */}
                <div className="absolute text-center" style={{ top: "70%", right: "0%", width: "30%" }}>
                  <p className="font-display font-bold text-sm md:text-lg text-[#5be0c3] leading-tight">Deseo</p>
                  <p className="text-[10px] md:text-xs text-white/85 leading-snug mt-1">Motivación, entusiasmo, compromiso</p>
                </div>

                {/* Intersección Foco ∩ Capacidades */}
                <div className="absolute text-center font-mono-caps text-[9px] md:text-[10px] tracking-[0.1em] uppercase text-white/85 leading-[1.2]" style={{ top: "44%", left: "19%", width: "16%" }}>
                  Podría<br />hacerlo
                </div>
                {/* Intersección Foco ∩ Deseo */}
                <div className="absolute text-center font-mono-caps text-[9px] md:text-[10px] tracking-[0.1em] uppercase text-white/85 leading-[1.2]" style={{ top: "44%", right: "19%", width: "16%" }}>
                  Lo haría
                </div>
                {/* Intersección Capacidades ∩ Deseo */}
                <div className="absolute text-center font-mono-caps text-[9px] md:text-[10px] tracking-[0.1em] uppercase text-white/85 leading-[1.2]" style={{ top: "72%", left: "50%", transform: "translateX(-50%)", width: "20%" }}>
                  Debo<br />hacerlo
                </div>

                {/* LO HAGO — centro de la intersección */}
                <div className="absolute" style={{ top: "56%", left: "50%", transform: "translate(-50%,-50%)" }}>
                  <span className="inline-flex items-center gap-2 rounded-full bg-black border border-[#ff8a3c] px-3 py-1.5 md:px-4 md:py-2 shadow-lg shadow-[#ff6a2b]/40">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#ff8a3c]" />
                    <span className="font-display font-black text-[#ffb46b] text-xs md:text-base tracking-[0.15em] uppercase">LO HAGO</span>
                  </span>
                </div>
              </div>
            </div>

            <p className="mt-10 md:mt-12 text-center max-w-3xl mx-auto text-white/70 text-base md:text-lg leading-relaxed">
              Falta una y el equipo se queda en <span className="text-white font-semibold">podría</span>, <span className="text-white font-semibold">lo haría</span> o <span className="text-white font-semibold">debo</span> — pero no lo hace.
            </p>
          </div>

          {/* Bloque — 03 · A dónde llegamos */}
          <div className="mt-14 md:mt-20">
            <p className="font-mono-caps text-xs tracking-[0.3em] uppercase text-[#ff8a3c] mb-6">
              03 · <span className="text-white/50">A dónde llegamos</span>
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-start">
              <div className="lg:col-span-7">
                <h3 className="font-display font-black text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.05] text-white max-w-3xl">
                  Compromiso, clima y productividad. Cuando se vuelven conscientes, aparece el <span className="text-gradient-music">orgullo</span>.
                </h3>
                <ul className="mt-8 space-y-4">
                  {[
                    { k: "Compromiso", v: "me apropio de los objetivos" },
                    { k: "Clima", v: "me siento valorado y respetado" },
                    { k: "Productividad", v: "se dan los resultados" },
                  ].map((r) => (
                    <li key={r.k} className="flex items-start gap-3 text-white/80 text-base md:text-lg">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#ff8a3c] shrink-0" />
                      <span>
                        <span className="text-[#ffb46b] font-semibold">{r.k}:</span> {r.v}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="lg:col-span-5">
                <div className="relative rounded-3xl border border-[#ff8a3c]/30 bg-gradient-to-br from-[#ff6a2b]/12 via-black to-black p-8 md:p-10 overflow-hidden">
                  <span className="absolute -top-24 -right-16 w-56 h-56 rounded-full bg-[#ff6a2b]/25 blur-3xl pointer-events-none" />
                  <div className="relative">
                    <p className="font-mono-caps text-xs tracking-[0.3em] uppercase text-white/50">— Referencia —</p>

                    {/* Gráfica de barras */}
                    <div className="mt-8 flex items-end justify-center gap-8 md:gap-12">
                      {/* Barra izquierda — Equipo promedio */}
                      <div className="flex flex-col items-center">
                        <span className="font-display font-black text-white/60 text-2xl md:text-3xl leading-none mb-3">100</span>
                        <div
                          className="w-16 md:w-20 rounded-t-md"
                          style={{
                            height: "160px",
                            background: "linear-gradient(to top, #3d2416, #5a3a26)",
                          }}
                        />
                        <span className="mt-3 font-mono-caps text-[10px] md:text-xs tracking-[0.18em] uppercase text-white/60 text-center leading-tight">Equipo<br />promedio</span>
                      </div>

                      {/* Barra derecha — Equipo comprometido */}
                      <div className="flex flex-col items-center">
                        <span className="inline-flex items-center rounded-full bg-[#ff6a2b] text-black px-3 py-1 font-mono-caps text-[10px] md:text-xs tracking-[0.18em] uppercase font-bold mb-2">+30%</span>
                        <span className="font-display font-black text-[#ffb46b] text-2xl md:text-3xl leading-none mb-3">130</span>
                        <div
                          className="w-16 md:w-20 rounded-t-md shadow-lg shadow-[#ff6a2b]/40"
                          style={{
                            height: "208px",
                            background: "linear-gradient(to top, #ff6a2b, #ff8a3c, #ffb46b)",
                          }}
                        />
                        <span className="mt-3 font-mono-caps text-[10px] md:text-xs tracking-[0.18em] uppercase text-white/85 text-center leading-tight">Equipo<br />comprometido</span>
                      </div>
                    </div>

                    {/* Línea base */}
                    <div className="mt-1 h-px bg-white/10" />

                    <p className="mt-6 text-white/75 text-base md:text-lg leading-relaxed">
                      Un equipo altamente comprometido puede mejorar su productividad <span className="text-[#ff8a3c] font-bold">hasta un 30%.</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bloque — 04 · El Acompañamiento 12 meses */}
          <div className="mt-14 md:mt-20">
            <p className="font-mono-caps text-xs tracking-[0.3em] uppercase text-[#ff8a3c] mb-6">
              04 · <span className="text-white/50">El acompañamiento — 12 meses</span>
            </p>
            <h3 className="font-display font-black text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.05] max-w-4xl text-white">
              Cultura no es un evento en el calendario. <span className="text-gradient-music">Es el calendario.</span>
            </h3>

            {(() => {
              const MONTHS = ["1","2","3","4","5","6","7","8","9","10","11","12"];
              const ROWS = [
                { name: "Estrategia", color: "#ff8a3c", bg: "rgba(255,138,60,0.18)", border: "rgba(255,138,60,0.55)", blocks: { 0: "Diagnóstico", 1: "Alineación", 5: "Seguimiento", 6: "Seguimiento", 11: "Cierre" } },
                { name: "Indicadores", color: "#d4b23c", dotAll: true },
                { name: "Encuentros", color: "#2ec9a8", bg: "rgba(46,201,168,0.18)", border: "rgba(46,201,168,0.55)", blocks: { 2: "Comité", 4: "Forecast", 6: "Comité", 8: "Resultados", 10: "Cierre" } },
                { name: "Reconocimiento", color: "#a78bfa", stars: [5, 9] },
              ];
              return (
                <div className="mt-10 md:mt-14">
                  {/* Hint mobile */}
                  <div className="md:hidden mb-3 flex items-center justify-end gap-2 font-mono-caps text-[10px] tracking-[0.2em] uppercase text-white/50">
                    <span>Desliza</span>
                    <ArrowRight className="w-3.5 h-3.5 animate-pulse" />
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-neutral-950 p-4 md:p-6 overflow-x-auto">
                    <div className="min-w-[880px]">
                      <div className="grid" style={{ gridTemplateColumns: "160px repeat(12, minmax(0,1fr))" }}>
                        <div />
                        {MONTHS.map((m, i) => (
                          <div key={i} className="text-center font-mono-caps text-[11px] tracking-[0.15em] uppercase text-white/40 pb-3 border-b border-white/10">{m}</div>
                        ))}
                      </div>
                      {ROWS.map((row) => (
                        <div key={row.name} className="grid items-center py-3 border-b border-white/5" style={{ gridTemplateColumns: "160px repeat(12, minmax(0,1fr))" }}>
                          <div className="pr-3">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full" style={{ background: row.color }} />
                              <span className="font-mono-caps text-[11px] md:text-xs tracking-[0.15em] uppercase text-white/85">{row.name}</span>
                            </div>
                          </div>
                          {MONTHS.map((_, i) => (
                            <div key={i} className="px-0.5 h-10 flex items-center justify-center">
                              {row.dotAll && (<span className="w-1.5 h-1.5 rounded-full" style={{ background: row.color, opacity: 0.7 }} />)}
                              {row.blocks?.[i] && (
                                <span
                                  className="w-full h-8 rounded-md border flex items-center justify-center font-mono-caps tracking-[0.04em] uppercase text-white/90 px-0.5 text-center leading-[1.05] whitespace-nowrap"
                                  style={{ background: row.bg, borderColor: row.border, fontSize: "9px" }}
                                  title={row.blocks[i]}
                                >
                                  {row.blocks[i]}
                                </span>
                              )}
                              {row.stars?.includes(i) && (
                                <svg viewBox="0 0 24 24" fill={row.color} className="w-5 h-5 drop-shadow-[0_0_6px_rgba(167,139,250,0.55)]">
                                  <path d="M12 2l2.9 6.5 7.1.7-5.4 4.8 1.6 7L12 17.5 5.8 21l1.6-7L2 9.2l7.1-.7L12 2z" />
                                </svg>
                              )}
                            </div>
                          ))}
                        </div>
                      ))}
                      <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2">
                        {ROWS.map((row) => (
                          <div key={row.name} className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-sm" style={{ background: row.color }} />
                            <span className="font-mono-caps text-[10px] md:text-xs tracking-[0.15em] uppercase text-white/60">{row.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="relative bg-black py-20 md:py-28 px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center rounded-3xl border border-[#ff6a2b]/30 bg-gradient-to-br from-[#ff6a2b]/12 via-black to-black p-8 md:p-16">
          <h2 className="font-display font-black text-3xl md:text-4xl lg:text-5xl leading-tight text-white">
            ¿Qué quieres que tu equipo <span className="text-gradient-music">sienta, piense y haga diferente</span>?
          </h2>
          <p className="mt-6 text-white/70 text-base md:text-lg max-w-xl mx-auto">
            Activemos transformaciones en tu equipo.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link
              to="/contact"
              className="btn-shine inline-flex items-center gap-3 rounded-full bg-white text-black pl-6 pr-5 py-3.5 font-mono-caps text-xs tracking-[0.2em] uppercase hover:bg-neutral-100 transition-colors"
            >
              Quiero mover a mi equipo
              <span className="w-7 h-7 rounded-full bg-[#ff6a2b] text-black flex items-center justify-center"><ArrowRight className="w-3.5 h-3.5" /></span>
            </Link>
          </div>
        </div>
      </section>

      <Footer footer={content.footer} />
      <WhatsAppButton />
    </div>
  );
}
